import { useEffect, useState } from 'react';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

/**
 * Custom hook to initialize and manage Monday SDK
 *
 * @returns {Object} Monday SDK instance and initialization status
 * @property {typeof monday} sdk - The Monday SDK instance
 * @property {boolean} isInitialized - Whether the SDK has been initialized
 */
export const useMondaySDK = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    monday.listen('context', (res) => {
      console.log('Monday context:', res);
      setIsInitialized(true);
    });

    // Notify Monday that the app is ready
    monday.execute('valueCreatedForUser');
  }, []);

  return {
    sdk: monday,
    isInitialized,
  };
};

/**
 * Hook to save settings to Monday storage
 *
 * @returns {Function} Function to save settings to Monday storage
 */
export const useSaveSettings = () => {
  const { sdk } = useMondaySDK();

  /**
   * Saves settings to Monday storage
   *
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @returns {Promise<void>}
   */
  const saveSettings = async (key: string, value: unknown): Promise<void> => {
    try {
      await sdk.storage.instance.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving settings:', error);
      throw error;
    }
  };

  return saveSettings;
};

/**
 * Hook to load settings from Monday storage
 *
 * @returns {Function} Function to load settings from Monday storage
 */
export const useLoadSettings = () => {
  const { sdk } = useMondaySDK();

  /**
   * Loads settings from Monday storage
   *
   * @param {string} key - Storage key
   * @returns {Promise<T | null>} Retrieved value or null if not found
   */
  const loadSettings = async <T>(key: string): Promise<T | null> => {
    try {
      const result = await sdk.storage.instance.getItem(key);
      if (result.data?.value) {
        return JSON.parse(result.data.value) as T;
      }
      return null;
    } catch (error) {
      console.error('Error loading settings:', error);
      throw error;
    }
  };

  return loadSettings;
};

/**
 * Board interface from Monday.com
 */
export interface MondayBoard {
  id: string;
  name: string;
}

/**
 * Hook to fetch boards from Monday.com
 *
 * @returns {Object} Boards data and loading state
 */
export const useFetchBoards = () => {
  const { sdk } = useMondaySDK();
  const [boards, setBoards] = useState<MondayBoard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetches boards from Monday.com using GraphQL API
   *
   * @returns {Promise<void>}
   */
  const fetchBoards = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const query = `query {
        boards (limit: 100) {
          id
          name
        }
      }`;

      const response = await sdk.api(query);

      if (response.data?.boards) {
        setBoards(response.data.boards);
      }
    } catch (err) {
      console.error('Error fetching boards:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch boards'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBoards();
  }, []);

  return {
    boards,
    isLoading,
    error,
    refetch: fetchBoards,
  };
};
